function pad(value) {
    return String(value).padStart(2, '0');
}
function formatOffset(offsetMinutes) {
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMinutes);
    return `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}
/**
 * The device's IANA zone, or undefined when the runtime cannot supply one.
 * Guarded because a host without full Intl throws here, and signing must not
 * fail just because the zone is unavailable.
 */
function resolveIanaZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    }
    catch {
        return undefined;
    }
}
/**
 * The UTC offset the named zone actually implies at `at`, or null if it cannot
 * be determined. Used to check the reported zone against the reported offset:
 * they are read from two independent sources, so a device can report
 * `timeZone: 'UTC'` while genuinely sitting at +05:30.
 */
function zoneOffsetMinutes(zone, at) {
    try {
        const name = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' })
            .formatToParts(at)
            .find((part) => part.type === 'timeZoneName')?.value;
        if (!name) {
            return null;
        }
        if (name === 'GMT' || name === 'UTC') {
            return 0;
        }
        const match = /^(?:GMT|UTC)([+-])(\d{2}):?(\d{2})$/.exec(name);
        if (!match) {
            return null;
        }
        return (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3]));
    }
    catch {
        return null;
    }
}
/**
 * Attested wall-clock fields from the signing device. Server UTC remains the
 * ordering key; these values are evidence of what the signer saw.
 *
 * The zone and the offset come from two independent sources, so they are
 * reconciled before being attested. A device reporting `UTC` while actually at
 * +05:30 would otherwise have its correct instant rendered in the wrong zone -
 * 10:00 AM (UTC) for a signature the signer applied at 15:30. When they
 * disagree, or no zone is available, all three fields are withheld: SSP-3906
 * requires an IANA identifier (a numeric offset cannot distinguish a
 * daylight-saving offset from a standard one) and requires the local time to be
 * marked unavailable rather than substituted, so the manifestation falls back
 * to UTC instead of claiming a wall-clock it cannot substantiate.
 */
export function getDeviceClockAttestation(now = new Date()) {
    const ianaZone = resolveIanaZone();
    if (!ianaZone) {
        return {};
    }
    const offsetMinutes = -now.getTimezoneOffset();
    if (zoneOffsetMinutes(ianaZone, now) !== offsetMinutes) {
        return {};
    }
    const utcOffset = formatOffset(offsetMinutes);
    return {
        ec__device_iana_zone: ianaZone,
        ec__device_utc_offset: utcOffset,
        ec__device_local_time: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}${utcOffset}`,
    };
}
